import { TextProps } from '@visx/text/lib/Text';
import { orderBy } from 'lodash';
import moment, { unitOfTime } from 'moment';

import theme from 'theme';

import { LineDataPointInterface, LineDataPointInterfaceWithDatum } from 'models/GraphModels';

import { TypographyColor, VariantLiteral } from 'components/_common/Typography/Typography';

import { DatedDatumType, DiseaseActivityDatumType } from './DiseaseActivityModels';

// LINK: https://observablehq.com/@d3/moving-average
// NOTE: this code has been altered to address corner cases
export function movingAverage(values: number[], N: number): number[] {
  let sum = 0;
  let denom = 0;
  const means: number[] = new Array(values.length).fill(NaN);
  for (let i = 0; i < values.length; i++) {
    sum += values[i] < 0 ? 0 : values[i];
    if (i >= N) sum -= values[i - N] < 0 ? 0 : values[i - N];
    if (denom < N) denom++;
    means[i] = sum / denom;
  }
  return means;
}

export function computeMovingAverage(
  graphData2: DiseaseActivityDatumType[],
  movingAverageWindow: number
): DiseaseActivityDatumType[] {
  const casesMovAvg = movingAverage(
    (graphData2 || []).map(x => x.series1),
    movingAverageWindow
  );
  return (graphData2 || []).map(
    (d, i) =>
      ({
        date: d.date,
        series1: casesMovAvg[i],
      } as DiseaseActivityDatumType)
  );
}

export function computeIfNeedToShowMovingAverage(
  graphData2: DiseaseActivityDatumType[],
  minDate: Date,
  maxDate: Date,
  threshold: number,
  aggregationDays: number
): boolean {
  const nDays = moment(maxDate).diff(moment(minDate), 'days');
  const nSlots = nDays / aggregationDays;
  const dataRatio = (graphData2 || []).length / nSlots;
  return dataRatio >= threshold;
}

// NOTE: it is assumed that minDate/maxDate/all graph points are all "startOf('day') UTC"
export function IterateWithinDateWindow<TDatum extends DatedDatumType>(
  graphData: TDatum[],
  minDate: Date,
  maxDate: Date,
  aggregationDays: number = 1,
  iterateGapsAtTheEnd: boolean = false
): TDatum[] {
  const graphData2 = orderBy(graphData, x => x.date).filter(
    d => minDate <= d.date && d.date <= maxDate
  );
  function* generator(): Iterable<TDatum> {
    // NOTE: we iterate gaps first so they don't obstruct the actual data elements during mouseover
    if (iterateGapsAtTheEnd) {
      let lastDate = moment(minDate);
      for (let i = 0; i < graphData2.length; i++) {
        const element = graphData2[i];
        const curDate = moment(element.date);
        while (curDate.diff(lastDate, 'days') / aggregationDays >= 0.5) {
          yield {
            date: lastDate.toDate(),
            dateEnd:
              aggregationDays <= 1
                ? lastDate.toDate()
                : lastDate
                    .clone()
                    .add(aggregationDays - 1, 'days')
                    .toDate(),
            isGhost: true,
          } as TDatum;
          lastDate.add(aggregationDays, 'days');
        }
        lastDate = curDate;
        lastDate.add(aggregationDays, 'days');
      }
      const curDate = moment(maxDate);
      while (curDate.diff(lastDate, 'days') / aggregationDays >= 0) {
        yield {
          date: lastDate.toDate(),
          dateEnd:
            aggregationDays <= 1
              ? lastDate.toDate()
              : lastDate
                  .clone()
                  .add(aggregationDays - 1, 'days')
                  .toDate(),
          isGhost: true,
        } as TDatum;
        lastDate.add(aggregationDays, 'days');
      }
    }
    for (const item of graphData2) {
      yield item;
    }
  }
  return Array.from(generator());
}

export const PopupArrowBottom = `M7.19141 8.51448L-0.000983185 8.51431L5.66722 0.793033C6.44206 -0.262456 7.94075 -0.262456 8.71558 0.793032L14.3838 8.51431L7.19141 8.51448Z`;
export const BigGraphLoadingSkeleton = `
<path d="M8.02979 57.38C8.02979 56.6178 8.64763 56 9.40979 56H13.6498C14.4119 56 15.0298 56.6178 15.0298 57.38V104H8.02979V57.38Z" fill="#EDF0F2"/>
<path d="M0 65.38C0 64.6178 0.617847 64 1.38 64H5.62C6.38215 64 7 64.6178 7 65.38V104H0V65.38Z" fill="#EDF0F2"/>
<path d="M16.0596 51.38C16.0596 50.6178 16.6774 50 17.4396 50H21.6796C22.4417 50 23.0596 50.6178 23.0596 51.38V104H16.0596V51.38Z" fill="#EDF0F2"/>
<path d="M32.1191 39.38C32.1191 38.6178 32.737 38 33.4991 38H37.7391C38.5013 38 39.1191 38.6178 39.1191 39.38V104H32.1191V39.38Z" fill="#EDF0F2"/>
<path d="M48.1787 57.38C48.1787 56.6178 48.7966 56 49.5587 56H53.7987C54.5609 56 55.1787 56.6178 55.1787 57.38V104H48.1787V57.38Z" fill="#EDF0F2"/>
<path d="M64.2383 59.38C64.2383 58.6178 64.8561 58 65.6183 58H68.8583C69.6204 58 70.2383 58.6178 70.2383 59.38V104H64.2383V59.38Z" fill="#EDF0F2"/>
<path d="M118.447 68.38C118.447 67.6178 119.065 67 119.827 67H124.067C124.829 67 125.447 67.6178 125.447 68.38V104H118.447V68.38Z" fill="#EDF0F2"/>
<path d="M182.685 72.38C182.685 71.6178 183.303 71 184.065 71H188.305C189.067 71 189.685 71.6178 189.685 72.38V104H182.685V72.38Z" fill="#EDF0F2"/>
<path d="M213.804 81.38C213.804 80.6178 214.422 80 215.184 80H219.424C220.186 80 220.804 80.6178 220.804 81.38V104H213.804V81.38Z" fill="#EDF0F2"/>
<path d="M24.0894 45.38C24.0894 44.6178 24.7072 44 25.4694 44H29.7094C30.4715 44 31.0894 44.6178 31.0894 45.38V104H24.0894V45.38Z" fill="#EDF0F2"/>
<path d="M40.1489 45.38C40.1489 44.6178 40.7668 44 41.5289 44H45.7689C46.5311 44 47.1489 44.6178 47.1489 45.38V104H40.1489V45.38Z" fill="#EDF0F2"/>
<path d="M56.2085 50.38C56.2085 49.6178 56.8263 49 57.5885 49H61.8285C62.5906 49 63.2085 49.6178 63.2085 50.38V104H56.2085V50.38Z" fill="#EDF0F2"/>
<path d="M150.566 45.38C150.566 44.6178 151.184 44 151.946 44H156.186C156.948 44 157.566 44.6178 157.566 45.38V104H150.566V45.38Z" fill="#EDF0F2"/>
<path d="M86.3276 50.38C86.3276 49.6178 86.9455 49 87.7076 49H91.9476C92.7098 49 93.3276 49.6178 93.3276 50.38V104H86.3276V50.38Z" fill="#EDF0F2"/>
<path d="M102.387 50.38C102.387 49.6178 103.005 49 103.767 49H108.007C108.769 49 109.387 49.6178 109.387 50.38V104H102.387V50.38Z" fill="#EDF0F2"/>
<path d="M166.625 59.38C166.625 58.6178 167.243 58 168.005 58H172.245C173.008 58 173.625 58.6178 173.625 59.38V104H166.625V59.38Z" fill="#EDF0F2"/>
<path d="M198.745 65.38C198.745 64.6178 199.362 64 200.125 64H203.365C204.127 64 204.745 64.6178 204.745 65.38V104H198.745V65.38Z" fill="#EDF0F2"/>
<path d="M134.506 69.38C134.506 68.6178 135.124 68 135.886 68H140.126C140.889 68 141.506 68.6178 141.506 69.38V104H134.506V69.38Z" fill="#EDF0F2"/>
<path d="M71.2681 68.38C71.2681 67.6178 71.8859 67 72.6481 67H75.8881C76.6502 67 77.2681 67.6178 77.2681 68.38V104H71.2681V68.38Z" fill="#EDF0F2"/>
<path d="M229.864 72.38C229.864 71.6178 230.482 71 231.244 71H235.484C236.246 71 236.864 71.6178 236.864 72.38V104H229.864V72.38Z" fill="#EDF0F2"/>
<path d="M126.477 61.38C126.477 60.6178 127.094 60 127.857 60H132.097C132.859 60 133.477 60.6178 133.477 61.38V104H126.477V61.38Z" fill="#EDF0F2"/>
<path d="M190.715 59.38C190.715 58.6178 191.333 58 192.095 58H196.335C197.097 58 197.715 58.6178 197.715 59.38V104H190.715V59.38Z" fill="#EDF0F2"/>
<path d="M221.834 76.38C221.834 75.6178 222.452 75 223.214 75H227.454C228.216 75 228.834 75.6178 228.834 76.38V104H221.834V76.38Z" fill="#EDF0F2"/>
<path d="M158.596 57.38C158.596 56.6178 159.214 56 159.976 56H164.216C164.978 56 165.596 56.6178 165.596 57.38V104H158.596V57.38Z" fill="#EDF0F2"/>
<path d="M94.3574 41.38C94.3574 40.6178 94.9753 40 95.7374 40H99.9774C100.74 40 101.357 40.6178 101.357 41.38V104H94.3574V41.38Z" fill="#EDF0F2"/>
<path d="M110.417 61.38C110.417 60.6178 111.035 60 111.797 60H116.037C116.799 60 117.417 60.6178 117.417 61.38V104H110.417V61.38Z" fill="#EDF0F2"/>
<path d="M174.655 65.38C174.655 64.6178 175.273 64 176.035 64H180.275C181.037 64 181.655 64.6178 181.655 65.38V104H174.655V65.38Z" fill="#EDF0F2"/>
<path d="M205.774 72.38C205.774 71.6178 206.392 71 207.154 71H211.394C212.157 71 212.774 71.6178 212.774 72.38V104H205.774V72.38Z" fill="#EDF0F2"/>
<path d="M142.536 63.38C142.536 62.6178 143.154 62 143.916 62H148.156C148.918 62 149.536 62.6178 149.536 63.38V104H142.536V63.38Z" fill="#EDF0F2"/>
<path d="M78.2979 61.38C78.2979 60.6178 78.9157 60 79.6779 60H83.9179C84.68 60 85.2979 60.6178 85.2979 61.38V104H78.2979V61.38Z" fill="#EDF0F2"/>
<path d="M237.894 65.38C237.894 64.6178 238.511 64 239.274 64H243.514C244.276 64 244.894 64.6178 244.894 65.38V104H237.894V65.38Z" fill="#EDF0F2"/>
<path d="M253.953 65.38C253.953 64.6178 254.571 64 255.333 64H259.573C260.335 64 260.953 64.6178 260.953 65.38V104H253.953V65.38Z" fill="#EDF0F2"/>
<path d="M245.923 59.38C245.923 58.6178 246.541 58 247.303 58H251.543C252.305 58 252.923 58.6178 252.923 59.38V104H245.923V59.38Z" fill="#EDF0F2"/>
`;

export function getAxisTypography(
  variant: Extract<'label-small' | 'body2' | 'caption' | 'subtitle2' | 'h6', VariantLiteral>,
  color: TypographyColor
): TextProps {
  return {
    fontFamily: "'Source Sans Pro', 'Helvetica Neue', Arial, Helvetica, sans-serif",
    fill: theme.colors[color],
    textAnchor: 'start',
    ...(variant === 'label-small'
      ? {
          fontSize: '9px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '14px',
          letterSpacing: '0.4px',
          dy: '6px',
        }
      : variant === 'body2'
      ? {
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '20px',
          dy: '12px',
        }
      : variant === 'caption'
      ? {
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 'normal',
          lineHeight: '14px',
          dy: '6px',
        }
      : variant === 'subtitle2'
      ? {
          fontSize: '14px',
          fontStyle: 'normal',
          lineHeight: '18px',
          fontWeight: '600',
          dy: '10px',
        }
      : variant === 'h6'
      ? {
          fontSize: '16px',
          fontStyle: 'normal',
          lineHeight: '20px',
          fontWeight: '600',
          dy: '12px',
        }
      : {}),
  };
}

/**
 * Used alongside a transformation that takes the lineheight and an offset to recalculate
 * the size of the text container with the offset
 *
 * NOTE: Used alongside "transform(x, y)" to get the height of an xAxis tick label
 * for VISX graphs
 *
 * @param lineHeight the line height, either a number or a '${number}px' value
 * @param offset the y offset of the label
 * @returns a number the new height of the text
 */
export function axisTickLabelYOffset(lineHeight: number | string, offset: number) {
  if (!lineHeight) return lineHeight;

  const lineHeightAsNum =
    typeof lineHeight === 'string' ? Number(lineHeight.replace('px', '')) : lineHeight;

  return lineHeightAsNum + (offset ?? 0);
}

/**
 * Generator that creates a date iterable that gets the dates from the first date, stepping
 * the distance between each date as passed in
 * @param startDate the first date in the iterable
 * @param numSteps how many steps we are taking
 * @param stepSize the distance between each dates
 * @param stepUnit the unit of distance between each date
 */
function* tickGenerator(
  startDate: moment.Moment,
  numSteps: number,
  stepSize: number,
  stepUnit: unitOfTime.DurationConstructor
): Iterable<Date> {
  const start = startDate.clone();

  for (let i = 0; i <= numSteps; i++) {
    yield start.toDate();
    start.add(stepSize, stepUnit);
  }
}

/**
 * Generates and array of beginning of months between the passed in
 * min and max date
 *
 * @param minDate the minimum date of the range
 * @param maxDate the maximum date of the range
 * @returns an array of beginning of month dates within the min and max dates
 */
export function axisBeginningOfMonthTicks(minDate: Date, maxDate: Date): Date[] {
  if (!minDate || !maxDate) return [];

  const firstMonth = moment(minDate).startOf('month');
  const monthsDiff = moment(maxDate).startOf('month').diff(firstMonth, 'months');

  return Array.from(tickGenerator(firstMonth, monthsDiff, 1, 'month')).filter(
    x => minDate <= x && x <= maxDate
  );
}

/**
 * Generates an array of biweekly start of weeks starting with the
 * first start of week found in the range
 *
 * @param minDate the smallest date in the range
 * @param maxDate the largest rate in the range
 * @returns an array of biweekly start of weeks starting from the first beginning of week
 */
export function axisBiWeeklyTicks(minDate: Date, maxDate: Date) {
  if (!minDate || !maxDate) return [];

  const firstWeek =
    minDate === moment(minDate).startOf('week').toDate()
      ? moment(minDate)
      : moment(minDate).startOf('week').add(1, 'week');

  const biWeeklyDiff = moment(maxDate).startOf('week').diff(firstWeek, 'weeks') / 2 + 1;

  return Array.from(tickGenerator(firstWeek, biWeeklyDiff, 2, 'weeks')).filter(
    x => minDate <= x && x <= maxDate
  );
}

/**
 * Extends a data point line to the start and end dates.
 *
 * When extending to the start date, it adds 2 points which are of 0 value, which are the day before the
 * supplied first date in the line and the start date itself
 *
 * When extending to the end date it takes the last value and appends it to the array with that last Y
 * value and the x value being the end date
 *
 * @param dataLine The line to extend to the start and end date
 * @param startDate If added and the first date is beyond the start date, it will extend to this date
 * @param endDate If added and the last date is before the end date, it will extend to this date
 * @returns A data line that is extended to hit the start and end date
 */
export function extendDatumLineToEdges<T>(
  dataLine: LineDataPointInterfaceWithDatum<T>[],
  startDate?: Date,
  endDate?: Date
): LineDataPointInterfaceWithDatum<T>[] {
  if (!dataLine?.length) return [];

  const firstDate = moment(dataLine[0].x).startOf('day');

  const startExtention: LineDataPointInterfaceWithDatum<T>[] =
    startDate && firstDate > moment(startDate).startOf('day')
      ? [
          { x: moment(startDate).startOf('day').toDate(), y: 0, filledIn: true },
          { x: firstDate.subtract(1, 'day').startOf('day').toDate(), y: 0, filledIn: true },
        ]
      : [];

  const lastDataPoint = dataLine[dataLine.length - 1];

  const endExtension: LineDataPointInterfaceWithDatum<T>[] =
    endDate && moment(lastDataPoint.x).startOf('day') < moment(endDate).startOf('day')
      ? [
          {
            x: moment(endDate).startOf('day').toDate(),
            y: lastDataPoint.y,
            lowY: lastDataPoint.lowY,
            highY: lastDataPoint.highY,
            filledIn: true,
            datum: lastDataPoint.datum,
          },
        ]
      : [];

  return [...startExtention, ...dataLine, ...endExtension];
}

/**
 * Fills in an array of x/y points with missing data.
 * Will carry over the last data point and add a "filledIn" param
 * to denote that this data has been filled in.
 *
 * @param data the array of x/y points to fill in blank dates
 * @returns an array of x/y points with filled in dates
 */
export function fillInDatumLineArray<T>(
  data: LineDataPointInterfaceWithDatum<T>[]
): LineDataPointInterfaceWithDatum<T>[] {
  if (!data?.length) return [];
  if (data.length === 1) return data;

  const res = [];
  let lastYValue: number = null;

  for (let i = 0; i < data.length; i++) {
    // Handles if this y value is null, then carry forward previous Y
    const dataPoint =
      lastYValue === null && data[i].y === null
        ? { ...data[i], y: 0, filledIn: true }
        : lastYValue !== null && data[i].y === null
        ? { ...data[i], y: lastYValue, filledIn: true }
        : data[i];

    res.push(dataPoint);

    lastYValue = dataPoint.y ?? 0;

    // Fills in empty dates between current date and next
    if (data[i + 1]) {
      const date = moment(dataPoint.x).add(1, 'day');
      while (date < moment(data[i + 1].x)) {
        res.push({
          x: date.toDate(),
          y: dataPoint.y,
          lowY: dataPoint.lowY,
          highY: dataPoint.highY,
          filledIn: true,
          datum: dataPoint.datum,
        });
        date.add(1, 'day');
      }
    }
  }

  return res;
}

/**
 * Goes backwards through an array to see if the number of filled in data is greater than the
 * threshold of reliability. If it is greater it will return false, otherwise true
 *
 * @param data A sorted array of the dates to go back to check through to see if it was fileld in
 * @param filledInThreshold The number of days in which if it's above that the data is not considered reliable
 * @param prevDaysToCheck How many days to go back into the array
 * @returns a boolean stating if too many days are filled in
 */
export function isReliableProjectionData(
  data: LineDataPointInterface[],
  filledInThreshold: number,
  prevDaysToCheck: number
) {
  if (!data?.length || data.length < prevDaysToCheck || data.length < filledInThreshold)
    return false;

  let numFilledIn = 0;

  for (let i = 1; i <= prevDaysToCheck; i++) {
    if (data[data.length - i].filledIn) numFilledIn++;
  }

  return numFilledIn <= filledInThreshold;
}

/**
 * Finds the days starting from the anchorDate separating the dates by the supplied
 * diff units and the number of units between dates that are supplied and returns
 * an array of those dates between the lower bounds and upper date bounds
 *
 * @param anchorDate the point to generate out from
 * @param minDate the lower bounds of the date markers
 * @param maxDate the upper bounds of the date markers
 * @param diffBetweenMarkers The number of diffUnits between marker days
 * @param markerDiffUnit The diff unit between markers EG: months, days, ect
 * @returns array of dates for markers
 */
export function generateMarkerDays(
  anchorDate: Date,
  minDate: Date,
  maxDate: Date,
  diffBetweenMarkers: number,
  markerDiffUnit: moment.unitOfTime.DurationConstructor
): Date[] {
  if (!anchorDate || !minDate || !maxDate || !diffBetweenMarkers) return [];

  const markerDays: Date[] = [];
  let lowDate = anchorDate;
  let highDate = moment(anchorDate).add(diffBetweenMarkers, markerDiffUnit).toDate();

  while (lowDate >= minDate || highDate <= maxDate) {
    if (lowDate >= minDate) {
      markerDays.unshift(lowDate);
      lowDate = moment(lowDate).subtract(diffBetweenMarkers, markerDiffUnit).toDate();
    }
    if (highDate <= maxDate) {
      markerDays.push(highDate);
      highDate = moment(highDate).add(diffBetweenMarkers, markerDiffUnit).toDate();
    }
  }

  return markerDays;
}
